import { getClassLevel, normalizeClassId } from '../../../shared/rules/classIdentity.mjs';
import { getClassSpellLimits } from '../../../shared/rules/spellPreparation';
import { CLASS_CASTING_PROFILES } from '../../../shared/rules/classSpellcasting.mjs';
import { SPELL_SLOT_LEVELS, getSpellSlotProfile } from '../../../shared/rules/spellSlots.mjs';
import { getChannelDivinityProfile } from '../../../shared/rules/channelDivinity.mjs';
import { bardicDieSides } from '../../../shared/rules/bard.mjs';
import { divineSparkDice, hasClericFeature } from '../../../shared/rules/cleric.mjs';
import { PRESERVE_LIFE_FEATURE_ID, preserveLifePool } from '../../../shared/rules/subclasses/cleric/life.mjs';
import { hasSubclassFeature } from '../../../shared/rules/subclasses/index.mjs';
import { rageProfile } from '../../../shared/rules/barbarianRage.mjs';
import { fighterMasteryCount, getActionSurgeProfile, getSecondWindProfile, indomitableUses } from '../../../shared/rules/fighter.mjs';
import { getMonkFocusPoints, martialArtsDie, monkMovementBonus } from '../../../shared/rules/monk.mjs';
import { getLayOnHandsProfile, hasPaladinFeature } from '../../../shared/rules/paladin.mjs';
import { auraOfProtectionRadius } from '../../../shared/rules/effects/auras.mjs';
import { favoredEnemyUses } from '../../../shared/rules/ranger.mjs';
import { cunningStrikeMaxEffects, sneakAttackDiceCount } from '../../../shared/rules/cunningStrike.mjs';
import { wildShapeUses } from '../../../shared/rules/druid.mjs';
import { BARBARIAN_PROGRESSION } from './barbarian';

const level = (character, classId) => getClassLevel(character, classId);
const dice = (count, sides) => (count ? `${count}d${sides}` : 0);
const signed = (value) => (value ? `+${value}` : 0);
const feet = (value) => (value ? `${value} ft` : 0);

// What each class's existing features are worth at a level. A value of 0 means the feature isn't there yet: the level
// that first grants it shows it as a new feature instead.
const CLASS_IMPROVEMENTS = Object.freeze({
  barbarian: [
    ['Rage uses', (c) => rageProfile(c).maximum],
    ['Rage Damage', (c) => signed(BARBARIAN_PROGRESSION[level(c, 'barbarian') - 1]?.rageDamage)],
    ['Weapon Mastery weapons', (c) => BARBARIAN_PROGRESSION[level(c, 'barbarian') - 1]?.weaponMasteryCount],
  ],
  bard: [['Bardic Inspiration die', (c) => `d${bardicDieSides(level(c, 'bard'))}`]],
  cleric: [
    ['Channel Divinity uses', (c) => getChannelDivinityProfile(c, 'cleric').maximum],
    ['Divine Spark', (c) => (hasClericFeature(c, 'channelDivinity') ? dice(divineSparkDice(level(c, 'cleric')), 8) : 0)],
    ['Preserve Life pool', (c) => (hasSubclassFeature(c, PRESERVE_LIFE_FEATURE_ID) ? preserveLifePool(level(c, 'cleric')) : 0)],
  ],
  druid: [['Wild Shape uses', (c) => wildShapeUses(level(c, 'druid'))]],
  fighter: [
    ['Second Wind uses', (c) => getSecondWindProfile(c).maximum],
    ['Action Surge uses', (c) => getActionSurgeProfile(c).maximum],
    ['Indomitable uses', (c) => indomitableUses(level(c, 'fighter'))],
    ['Weapon Mastery weapons', (c) => fighterMasteryCount(level(c, 'fighter'))],
  ],
  monk: [
    ['Focus Points', (c) => getMonkFocusPoints(c)],
    ['Martial Arts die', (c) => `d${martialArtsDie(c)}`],
    ['Unarmored Movement', (c) => (monkMovementBonus(c) ? `+${monkMovementBonus(c)} ft` : 0)],
  ],
  paladin: [
    ['Lay On Hands pool', (c) => getLayOnHandsProfile(c).maximum],
    ['Channel Divinity uses', (c) => getChannelDivinityProfile(c, 'paladin').maximum],
    ['Aura of Protection', (c) => (hasPaladinFeature(c, 'auraOfProtection') ? feet(auraOfProtectionRadius(level(c, 'paladin'))) : 0)],
  ],
  ranger: [['Favored Enemy free casts', (c) => favoredEnemyUses(level(c, 'ranger'))]],
  rogue: [
    ['Sneak Attack', (c) => dice(sneakAttackDiceCount(level(c, 'rogue')), 6)],
    ['Cunning Strike effects per hit', (c) => (level(c, 'rogue') >= 5 ? cunningStrikeMaxEffects(level(c, 'rogue')) : 0)],
  ],
});

const totalLevel = (character) => (character?.occupation || []).reduce((sum, entry) => sum + (Number(entry?.Level ?? entry?.level) || 0), 0);
// Damaging cantrips gain a die at character levels 5, 11 and 17, whichever class the level comes from.
const cantripDice = (character) => ((character?.spells || []).some((spell) => Number(spell?.level) === 0) ? 1 + [5, 11, 17].filter((tier) => totalLevel(character) >= tier).length : 0);
const spellImprovements = (classId) => [
  ['Cantrips known', (c) => getClassSpellLimits(classId, level(c, classId), c).cantrips],
  ['Prepared spells', (c) => getClassSpellLimits(classId, level(c, classId), c).preparedSpells],
];

/**
 * What the level improves in features the character already has, as [{ label, from, to }], read from the same rules
 * that run them. Only the class gaining the level counts, plus cantrip damage, which follows character level.
 */
export const levelImprovements = (form, nextForm, className) => {
  const classId = normalizeClassId(className);
  return [...(CLASS_IMPROVEMENTS[classId] || []), ...(CLASS_CASTING_PROFILES[classId] ? spellImprovements(classId) : []), ['Cantrip damage dice', cantripDice]]
    .map(([label, value]) => ({ label, from: value(form) || 0, to: value(nextForm) || 0 }))
    .filter(({ from, to }) => from && to && from !== to);
};

/**
 * The spell slots a caster has at the new level, as { slots: [{ level, count, gained }], pact }, or null for a class that
 * doesn't cast. Multiclass slots come from the shared caster-level table, so a level in one class can add any slot level.
 */
export const spellSlotGains = (form, nextForm, className) => {
  if (!CLASS_CASTING_PROFILES[normalizeClassId(className)]) return null;
  const before = getSpellSlotProfile(form), after = getSpellSlotProfile(nextForm);
  const slots = SPELL_SLOT_LEVELS.map((slotLevel) => ({ level: slotLevel, count: after.spellcasting.maximumByLevel[slotLevel], gained: after.spellcasting.maximumByLevel[slotLevel] - before.spellcasting.maximumByLevel[slotLevel] }))
    .filter(({ count }) => count > 0);
  const pact = after.pactMagic.maximum ? { level: after.pactMagic.slotLevel, count: after.pactMagic.maximum,
    gained: after.pactMagic.maximum - before.pactMagic.maximum, raised: after.pactMagic.slotLevel > before.pactMagic.slotLevel } : null;
  return slots.length || pact ? { slots, pact } : null;
};
